import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ujmsCaseFinance, ujmsCreateFinAccount, ujmsReverseTxn } from "../../services/ujmsApi";
import { FinanceActionDialog } from "../pages/FinanceDialogs";
import { Panel, Badge, thCls, tdCls, btnGhost, btnPrimary, fmtDT, can, EmptyState } from "../ui";
import { Banknote } from "lucide-react";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export const FinanceTab = ({ caseId, access }) => {
  const [data, setData] = useState(null);
  const [action, setAction] = useState(null);
  const manage = can(access, "finance.manage");

  const load = useCallback(() => {
    ujmsCaseFinance(caseId).then((r) => setData(r.data)).catch(() => setData({ accounts: [] }));
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const openAccount = async (accountType) => {
    try { await ujmsCreateFinAccount(caseId, { account_type: accountType }); toast.success("Account opened"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const reverse = async (t) => {
    const reason = window.prompt("Reason for reversal (required):");
    if (!reason) return;
    try { await ujmsReverseTxn(t.id, { reason }); toast.success("Transaction reversed on ledger"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="tab-finance">
      {manage && (
        <div className="flex gap-2">
          <button className={btnPrimary} onClick={() => openAccount("CASE_FEES")} data-testid="fin-open-fees-btn"><Banknote className="w-4 h-4" /> Open Fees Account</button>
          <button className={btnGhost} onClick={() => openAccount("RESTITUTION")} data-testid="fin-open-restitution-btn">Open Restitution Account</button>
        </div>
      )}
      {data.accounts.length === 0 && (
        <Panel title="Court finance" testId="fin-empty-panel">
          <EmptyState icon={Banknote} title="No financial accounts on this case"
            note="Fines and restitution imposed at sentencing create accounts automatically." testId="fin-empty" />
        </Panel>
      )}
      {data.accounts.map((a) => (
        <Panel key={a.id} testId={`fin-case-account-${a.id}`}
          title={`${a.account_type.replace(/_/g, " ")} — ${a.party_name || "Case"} · Balance ${money(a.balance)}`}
          action={manage && (
            <div className="flex gap-1.5">
              <button className={btnGhost} onClick={() => setAction({ type: "assess", account: a })} data-testid={`fin-tab-assess-${a.id}`}>Assess</button>
              <button className={`${btnGhost} border-emerald-400 text-emerald-700`} onClick={() => setAction({ type: "pay", account: a })} data-testid={`fin-tab-pay-${a.id}`}>Payment</button>
              <button className={btnGhost} onClick={() => setAction({ type: "adjust", account: a })} data-testid={`fin-tab-adjust-${a.id}`}>Adjust</button>
              <button className={btnGhost} onClick={() => setAction({ type: "waive", account: a })} data-testid={`fin-tab-waive-${a.id}`}>Waive</button>
            </div>
          )}>
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>When</th><th className={thCls}>Type</th><th className={thCls}>Amount</th>
              <th className={thCls}>Reason</th><th className={thCls}>Actor</th><th className={thCls}>Ref</th><th className={thCls}></th></tr></thead>
            <tbody>
              {a.transactions.map((t) => (
                <tr key={t.id} data-testid={`fin-ledger-row-${t.id}`}>
                  <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(t.created_at)}</td>
                  <td className={tdCls}><Badge value={t.type} />{t.reversed && <span className="ml-1 text-[10px] font-mono text-red-600">REVERSED</span>}</td>
                  <td className={`${tdCls} font-mono text-xs ${t.amount < 0 ? "text-emerald-700" : ""}`}>{t.amount < 0 ? "−" : "+"}{money(Math.abs(t.amount))}</td>
                  <td className={`${tdCls} text-xs max-w-xs truncate`}>{t.reason}</td>
                  <td className={`${tdCls} text-xs`}>{t.actor}</td>
                  <td className={`${tdCls} font-mono text-[10px]`}>{t.receipt_number || t.reference?.slice(0, 10) || "—"}</td>
                  <td className={tdCls}>{manage && !t.reversed && t.type !== "REVERSAL" &&
                    <button className={btnGhost} onClick={() => reverse(t)} data-testid={`fin-reverse-${t.id}`}>Reverse</button>}</td>
                </tr>
              ))}
              {!a.transactions.length && <tr><td colSpan={7} className={tdCls}>No ledger activity.</td></tr>}
            </tbody>
          </table>
        </Panel>
      ))}
      <FinanceActionDialog action={action} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); }} />
    </div>
  );
};
